import type React from "react"

import { HttpError } from "@/utils/api"

import { CloseRounded, InsightsRounded } from "@mui/icons-material"
import {
  Box,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from "@mui/material"

import { useGetDisaggregation } from "../../data/use-get-disaggregation"
import { Eyebrow, SectionTitle } from "../../homepage/atoms"
import { EnduseDeltaBars } from "./enduse-delta-bars"
import { EnduseDonut } from "./enduse-donut"
import { TypicalWeekStack } from "./typical-week-stack"
import type { AnomalousCycle } from "./variance.types"

export interface DisaggregationDrawerProps {
  siteId: string | null
  cycle: AnomalousCycle | null
  open: boolean
  onClose: () => void
}

/**
 * Right-side drawer mirroring `BillDrawer`'s shell — a PRISM end-use
 * disaggregation for an anomalous bill cycle (donut share, typical-week
 * stacked area, YoY per-EULP delta bars). The disaggregation leaf is still
 * keyed by calendar month, so `cycleId` derives from the cycle's
 * `service_period.end` — the ISO first-of-that-month, sliced to `YYYY-MM`.
 */
// Section header keyed to which SeasonalBaseline tier the delta used, so the
// comparison is labeled honestly (same-month vs a wider window vs all months).
const BASELINE_LABEL: Record<string, string> = {
  seasonal: "Vs. same month, other years",
  seasonal_window: "Vs. same season (±1 month)",
  flat: "Vs. typical month",
  none: "Vs. seasonal baseline",
}

/** ISO first-of-month (`YYYY-MM-01`) for the calendar month containing the
 * cycle's service-period end date. */
const cycleMonthStart = (cycle: AnomalousCycle): string =>
  `${cycle.service_period.end.slice(0, 7)}-01`

export const DisaggregationDrawer: React.FC<DisaggregationDrawerProps> = ({
  siteId,
  cycle,
  open,
  onClose,
}) => {
  const month = cycle ? cycleMonthStart(cycle) : null
  const cycleId = month ? month.slice(0, 7) : null
  const {
    disaggregationData: data,
    disaggregationIsPending: isPending,
    disaggregationError: error,
  } = useGetDisaggregation(open ? siteId : null, open ? cycleId : null)

  return (
    <Drawer anchor="right" onClose={onClose} open={open}>
      <Box
        sx={{
          width: 560,
          maxWidth: "100vw",
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          sx={{
            alignItems: "center",
            justifyContent: "space-between",
            p: 3,
            pb: 2,
          }}
        >
          <Box>
            <Eyebrow>Electric end-use disaggregation</Eyebrow>
            <SectionTitle>{data?.periodLabel ?? "…"}</SectionTitle>
          </Box>
          <IconButton aria-label="Close" onClick={onClose}>
            <CloseRounded />
          </IconButton>
        </Stack>
        <Divider />
        <Box sx={{ flex: 1, overflowY: "auto", p: 3 }}>
          {open && isPending && siteId && cycleId ? (
            <Stack
              direction="row"
              spacing={1.5}
              sx={{ alignItems: "center", py: 6, justifyContent: "center" }}
            >
              <CircularProgress size={20} />
              <Typography color="text.secondary" variant="body2">
                Loading disaggregation…
              </Typography>
            </Stack>
          ) : error ? (
            <Typography color="error" variant="body1">
              {error instanceof HttpError
                ? "Couldn't load end-use disaggregation. Please try again."
                : "Couldn't load end-use disaggregation."}
            </Typography>
          ) : data && !data.available ? (
            <Stack
              spacing={1}
              sx={{ py: 4, alignItems: "center", textAlign: "center" }}
            >
              <InsightsRounded color="disabled" sx={{ fontSize: 40 }} />
              <Typography color="text.secondary" variant="body2">
                End-use disaggregation needs AMI / PRISM silver for this cycle.
              </Typography>
            </Stack>
          ) : data ? (
            <Stack spacing={3}>
              <Box>
                <Eyebrow sx={{ mb: 1 }}>Electric end-use share</Eyebrow>
                <EnduseDonut enduses={data.enduses} totalKwh={data.totalKwh} />
              </Box>
              <Box>
                <Eyebrow sx={{ mb: 1 }}>
                  Typical week (electric kW by end-use)
                </Eyebrow>
                <TypicalWeekStack series={data.typicalWeek} />
              </Box>
              <Box>
                <Eyebrow sx={{ mb: 1 }}>
                  {BASELINE_LABEL[data.basis ?? "none"]}
                </Eyebrow>
                <EnduseDeltaBars enduses={data.enduses} />
              </Box>
            </Stack>
          ) : null}
        </Box>
      </Box>
    </Drawer>
  )
}
